import React from 'react';
import { Tooltip } from '@mui/material';

import { useEntitlementStatus } from '../hooks/useEntitlementStatus';

export interface EntitlementDisabledTooltipProps {
  /** The capability name to check */
  capabilityName: string;
  /** Child element to wrap (typically a Button or IconButton) */
  children: React.ReactElement<{ sx?: Record<string, unknown>; disabled?: boolean }>;
  /** Additional condition to show tooltip (combined with isBlocked) */
  showTooltip?: boolean;
}

/**
 * EntitlementDisabledTooltip Component
 *
 * Wraps a child element (typically a Button) with a tooltip that explains
 * why the element is disabled when the entitlement quota is depleted or
 * when the user is a non-clinician.
 *
 * Automatically disables the child element when:
 * - The entitlement quota is depleted, OR
 * - The user is a non-clinician (admin without clinician role) and the feature gate is enabled
 *
 * Handles the MUI pattern where disabled buttons need to be wrapped in a
 * span for tooltips to work properly.
 *
 * If the entitlement is not blocked, returns children as-is (no extra DOM nodes).
 *
 * @example
 * ```tsx
 * <EntitlementDisabledTooltip capabilityName="note_creation">
 *   <Button disabled={otherConditions}>
 *     Generate Note
 *   </Button>
 * </EntitlementDisabledTooltip>
 * ```
 */
export const EntitlementDisabledTooltip = React.memo<EntitlementDisabledTooltipProps>(
  ({ capabilityName, children, showTooltip = true }) => {
    const { isBlocked, tooltipMessage } = useEntitlementStatus(capabilityName);

    // If not blocked or no tooltip message, return children as-is
    if (!isBlocked || !tooltipMessage || !showTooltip) {
      return children;
    }

    // Extract flex value from child's sx prop for wrapper span
    const childSx = children.props.sx || {};
    const flexValue = childSx.flex as React.CSSProperties['flex'];

    // Clone child with disabled: true when blocked, and adjust flex if needed
    // Always clone when blocked since we need to inject the disabled prop
    const adjustedChild = React.cloneElement(children, {
      disabled: children.props.disabled || isBlocked,
      sx: flexValue !== undefined ? { ...childSx, flex: 1 } : childSx,
    });

    // Wrap in span for MUI tooltip to work with disabled buttons
    // The cloned child will always be disabled when blocked, so always wrap
    const wrappedChild =
      flexValue !== undefined ? (
        <span style={{ display: 'flex', flex: flexValue }}>{adjustedChild}</span>
      ) : (
        <span>{adjustedChild}</span>
      );

    return <Tooltip title={tooltipMessage}>{wrappedChild}</Tooltip>;
  },
);

EntitlementDisabledTooltip.displayName = 'EntitlementDisabledTooltip';
